/**
 * Karyawan per cabang (langkah 36 / HR-03, ISIAN §9 — vonis Owner 19/20 Sep 2026): FIELD ISIAN nama +
 * jabatan yang diisi MASING-MASING KEPALA CABANG; tidak ada seed bertoken; SEMUA wajib absen, termasuk
 * Kepala Cabang. Penulis backend: POST /api/hr/employees (Owner semua region, KC region sendiri).
 *
 * K203 / VONIS-27SEP-BUSINESS-56-65 (BUSINESS-58, Owner 27 Sep 2026):
 *   (a) Owner mendaftarkan & mencabut tablet kios — kode perangkat tampil SEKALI (PUT/POST /api/hr/kiosk-devices);
 *   (b) Kepala Cabang region mengatur/mengganti PIN karyawan — PIN tidak pernah bisa dilihat (PUT /employees/{id}/pin);
 *   (c) Kepala Cabang region (akun cabangnya) atau Owner menautkan karyawan ke akun login (PUT /employees/{id}/akun).
 * Penjaga sesungguhnya di backend (services/kios_pin_akun.py); tombol di sini hanya kenyamanan.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { useAuth } from "@/contexts/AuthContext";
import { konfirmasi, mintaTeks } from "@/lib/dialogAplikasi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useDaftar } from "@/hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "@/components/etams/PalangDaftar";
import { tautanTelegram } from "@/lib/papanKerjaApi";
import { pesanPendaftaranTelegram } from "@/lib/telegramDaftar";

const NAMA_PERAN = {
  OWNER: "Owner", KEPALA_CABANG: "Kepala Cabang", CS: "CS", ADMIN_PRODUKSI: "Admin Produksi",
  ADMIN_GUDANG: "Admin Gudang", ADMIN_PURCHASING: "Admin Purchasing", FINANCE: "Finance",
  DESIGNER_EDITOR: "Designer Editor", SPV_DESAINER: "SPV Desainer", PEMANTAU: "Pemantau",
};

//: Cermin `URUT_SAH_KARYAWAN` di backend/services/attendance_service.py.
const URUT_SAH = ["employee_code", "full_name", "position", "is_active"];
const LABEL_AKTIF = { true: "Aktif", false: "Nonaktif" };
const KOLOM_CSV = [
  { kunci: "employee_code", judul: "Kode" },
  { kunci: "full_name", judul: "Nama" },
  { kunci: "position", judul: "Jabatan" },
  { kunci: "punya_pin", judul: "PIN kios", ambil: (k) => (k.punya_pin ? "sudah diatur" : "belum ada") },
  { kunci: "is_active", judul: "Status", ambil: (k) => (k.is_active === false ? "nonaktif" : "aktif") },
];

function AturPin({ k, onSelesai }) {
  const [pin, setPin] = useState("");
  const [ulang, setUlang] = useState("");
  const [sibuk, setSibuk] = useState(false);
  const sah = /^\d{4,12}$/.test(pin) && pin === ulang;
  const simpan = async () => {
    setSibuk(true);
    try {
      const r = await api.put(`/hr/employees/${k.id}/pin`, { pin });
      toast.success(r.data?.diganti ? "PIN absen diganti." : "PIN absen diatur.");
      setPin(""); setUlang("");
      onSelesai();
    } catch (e) { toast.error(apiErrorMessage(e, "PIN tidak bisa disimpan")); }
    finally { setSibuk(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`hk-pin-form-${k.id}`}>
      <Input type="password" inputMode="numeric" autoComplete="new-password" placeholder="PIN baru (4–12 angka)"
             value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className="w-44"
             data-testid="hk-pin-input" />
      <Input type="password" inputMode="numeric" autoComplete="new-password" placeholder="Ulangi PIN"
             value={ulang} onChange={(e) => setUlang(e.target.value.replace(/\D/g, ""))} className="w-36"
             data-testid="hk-pin-ulang" />
      <Button size="sm" disabled={!sah || sibuk} onClick={simpan} data-testid="hk-pin-simpan">Simpan PIN</Button>
      <Button size="sm" variant="outline" onClick={onSelesai}>Batal</Button>
      {pin && ulang && pin !== ulang && <span className="text-xs text-destructive">PIN dan ulangannya belum sama.</span>}
    </div>
  );
}

function TabletKios({ regions, stores }) {
  const [daftar, setDaftar] = useState([]);
  const [baru, setBaru] = useState({ region_id: "", store_id: "", device_code: "", notes: "" });
  const [kode, setKode] = useState(null);
  const [sibuk, setSibuk] = useState(false);

  const muat = useCallback(async () => {
    try { const r = await api.get("/hr/kiosk-devices"); setDaftar(r.data.items || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Daftar tablet kios gagal dimuat")); }
  }, []);
  useEffect(() => { muat(); }, [muat]);

  const daftarkan = async () => {
    setSibuk(true);
    try {
      const body = { ...baru, device_code: baru.device_code.trim().toUpperCase() };
      Object.keys(body).forEach((x) => { if (body[x] === "") delete body[x]; });
      const r = await api.post("/hr/kiosk-devices", body);
      setKode({ nama: r.data.device_code, kode: r.data.kode_perangkat });
      setBaru({ region_id: baru.region_id, store_id: "", device_code: "", notes: "" });
      muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Tablet kios tidak bisa didaftarkan")); }
    finally { setSibuk(false); }
  };

  const cabut = async (d) => {
    const ok = await konfirmasi(`Cabut tablet ${d.device_code}? Tablet ini tidak bisa lagi dipakai absen sampai didaftarkan ulang.`,
      { judul: "Cabut tablet kios", ya: "Cabut", bahaya: true });
    if (!ok) return;
    const alasan = await mintaTeks("Alasan mencabut (boleh dikosongkan):", { judul: "Alasan", multiBaris: false });
    try {
      await api.post(`/hr/kiosk-devices/${d.id}/cabut`, { alasan: alasan || null });
      toast.success("Tablet kios dicabut.");
      muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Tablet kios tidak bisa dicabut")); }
  };

  const storeRegion = stores.filter((s) => !baru.region_id || s.region_id === baru.region_id);
  return (
    <Card className="space-y-3 p-4" data-testid="hr-tablet-kios">
      <div>
        <h2 className="text-lg font-semibold">Tablet kios absen</h2>
        <p className="text-sm text-muted-foreground">
          Daftarkan tablet yang dipasang di cabang untuk absen dengan kode karyawan dan PIN. Kode perangkat hanya tampil
          sekali saat tablet didaftarkan — ketik langsung di tablet itu.
        </p>
      </div>
      {kode && (
        <div className="rounded-md border border-primary bg-primary/5 p-3 text-sm" data-testid="hk-kios-kode-kotak">
          <p>Kode perangkat untuk tablet <b>{kode.nama}</b>:</p>
          <p className="my-2 select-all break-all font-mono text-base" data-testid="hk-kios-kode">{kode.kode}</p>
          <p className="text-xs text-muted-foreground">
            Kode ini tidak bisa dilihat lagi sesudah kotak ini ditutup. Kalau hilang, cabut tablet ini lalu daftarkan ulang.
          </p>
          <Button size="sm" className="mt-2" onClick={() => setKode(null)} data-testid="hk-kios-kode-tutup">Sudah diketik di tablet</Button>
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-4">
        <select className="rounded-md border bg-background px-2 py-2 text-sm" value={baru.region_id}
                onChange={(e) => setBaru({ ...baru, region_id: e.target.value, store_id: "" })} data-testid="hk-kios-region">
          <option value="">— pilih cabang —</option>
          {regions.map((r) => <option key={r.id} value={r.id}>{r.name || r.code}</option>)}
        </select>
        <select className="rounded-md border bg-background px-2 py-2 text-sm" value={baru.store_id}
                onChange={(e) => setBaru({ ...baru, store_id: e.target.value })} data-testid="hk-kios-store">
          <option value="">— cabang (boleh kosong) —</option>
          {storeRegion.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Input placeholder="Nama tablet (boleh kosong)" value={baru.device_code}
               onChange={(e) => setBaru({ ...baru, device_code: e.target.value })} data-testid="hk-kios-nama" />
        <Input placeholder="Catatan (boleh kosong)" value={baru.notes}
               onChange={(e) => setBaru({ ...baru, notes: e.target.value })} />
        <div className="sm:col-span-4">
          <Button disabled={!baru.region_id || sibuk} onClick={daftarkan} data-testid="hk-kios-daftar">Daftarkan tablet</Button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
          <tr><th className="px-3 py-2 text-left">Tablet</th><th className="px-3 py-2 text-left">Wilayah / cabang</th>
            <th className="px-3 py-2 text-left">Terakhir dipakai</th><th className="px-3 py-2 text-left">Status</th><th /></tr>
        </thead>
        <tbody>
          {daftar.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">Belum ada tablet kios.</td></tr>}
          {daftar.map((d) => (
            <tr key={d.id} className="border-t" data-testid={`hk-kios-baris-${d.id}`}>
              <td className="px-3 py-2 font-mono text-xs">{d.device_code}</td>
              <td className="px-3 py-2">{d.region?.name}{d.store?.name ? ` / ${d.store.name}` : ""}</td>
              <td className="px-3 py-2 text-xs">{d.last_seen_at ? new Date(d.last_seen_at).toLocaleString("id-ID") : "belum pernah"}</td>
              <td className="px-3 py-2 text-xs">{d.is_active ? "aktif" : "dicabut"}</td>
              <td className="px-3 py-2 text-right">
                {d.is_active && (
                  <Button size="sm" variant="outline" onClick={() => cabut(d)} data-testid={`hk-kios-cabut-${d.id}`}>Cabut</Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export default function HRKaryawan() {
  const { profile, isOwner, isKepalaCabang } = useAuth();
  const [baris, setBaris] = useState([]);
  const [total, setTotal] = useState(0);
  const [memuat, setMemuat] = useState(true);
  const [regions, setRegions] = useState([]);
  const [stores, setStores] = useState([]);
  const [akun, setAkun] = useState([]);
  const [baru, setBaru] = useState({ employee_code: "", full_name: "", position: "", region_id: "", store_id: "", phone: "" });
  const [sibuk, setSibuk] = useState(false);
  const [pinId, setPinId] = useState(null);
  const [pilihAkun, setPilihAkun] = useState({});
  // T60: tautan pendaftaran Telegram per karyawan (semua jabatan — CS & Kepala Cabang tidak punya tahap produksi).
  const [tautanTg, setTautanTg] = useState(null);
  const mintaTautanTg = async (k) => {
    setTautanTg(null);
    try {
      const { data } = await tautanTelegram(k.id);
      setTautanTg({ nama: data.nama, url: data.tautan, jam: data.berlaku_jam, perintah: data.perintah_cadangan });
    } catch (e) { toast.error(apiErrorMessage(e, "Tautan Telegram belum bisa dibuat.")); }
  };

  // Pola daftar seragam (28 Sep 2026): cari/urut/halaman di SERVER, keadaan di alamat halaman.
  // Saringan bawaan "Aktif" — karyawan nonaktif tampil bila chip-nya dihapus.
  const daftar = useDaftar(baris, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "employee_code", arah: "asc" },
    saringan: {
      aktif: { label: "Status", param: "active", bawaan: "true", tampil: (v) => LABEL_AKTIF[v] || v },
      ...(isOwner ? { region: { label: "Wilayah", param: "region_id",
        tampil: (v) => (regions.find((r) => r.id === v) || {}).name || "wilayah terpilih" } } : {}),
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({ ...paramSaringan, ...(cari && { q: cari }),
    limit, offset, sort, arah, ...o }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await api.get("/hr/employees", { params: bangunParams({ offset: off, limit: n }) })).data, [bangunParams]);

  const muatDaftar = useCallback(async () => {
    setMemuat(true);
    try {
      const r = await api.get("/hr/employees", { params: bangunParams() });
      setBaris(r.data.items || []);
      setTotal(typeof r.data.total === "number" ? r.data.total : (r.data.items || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Daftar karyawan gagal dimuat")); }
    finally { setMemuat(false); }
  }, [bangunParams]);
  const muatAkun = useCallback(async () => {
    try { const r = await api.get("/hr/akun-bisa-ditautkan"); setAkun(r.data.items || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Daftar akun login gagal dimuat")); }
  }, []);
  const muat = useCallback(() => { muatDaftar(); muatAkun(); }, [muatDaftar, muatAkun]);
  useEffect(() => { muatDaftar(); }, [muatDaftar]);
  useEffect(() => { muatAkun(); }, [muatAkun]);
  useEffect(() => {
    Promise.all([api.get("/regions"), api.get("/stores")]).then(([r, s]) => {
      setRegions(r.data?.items || r.data || []);
      setStores(s.data?.items || s.data || []);
    }).catch((e) => toast.error(apiErrorMessage(e, "Daftar cabang gagal dimuat")));
  }, []);

  const regionSaya = isOwner ? baru.region_id : profile?.region_id;
  const storeRegion = stores.filter((s) => !regionSaya || s.region_id === regionSaya);

  const simpan = async () => {
    setSibuk(true);
    try {
      const body = { ...baru, region_id: regionSaya, employee_code: baru.employee_code.trim().toUpperCase() };
      Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
      await api.post("/hr/employees", body);
      toast.success("Karyawan ditambahkan");
      setBaru({ employee_code: "", full_name: "", position: "", region_id: baru.region_id, store_id: "", phone: "" });
      muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Karyawan tidak bisa ditambahkan")); }
    finally { setSibuk(false); }
  };

  const tautkan = async (k, uid) => {
    try {
      await api.put(`/hr/employees/${k.id}/akun`, { user_profile_id: uid || null });
      toast.success(uid ? "Akun login ditautkan." : "Tautan akun login dilepas.");
      setPilihAkun((p) => ({ ...p, [k.id]: "" }));
      muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Tautan akun tidak bisa disimpan")); }
  };

  const lepas = async (k) => {
    const ok = await konfirmasi(`Lepas tautan akun login dari ${k.full_name}? Ia tidak bisa absen lewat HP sampai ditautkan lagi.`,
      { judul: "Lepas tautan akun", ya: "Lepas" });
    if (ok) tautkan(k, null);
  };

  const akunMilik = (k) => akun.find((a) => a.id === k.user_profile_id);
  const akunBebas = akun.filter((a) => !a.tertaut_employee_id);
  const bolehTaut = isOwner || isKepalaCabang;
  // T60: penerima tugas "WhatsApp ke customer belum sampai" (K218) = karyawan tertaut akun CS/Kepala Cabang yang
  // sudah terdaftar Telegram. Dihitung dari daftar yang sedang tampil.
  const penerimaTg = daftar.tampil.filter((k) =>
    ["CS", "KEPALA_CABANG"].includes(akunMilik(k)?.peran) && k.telegram_terdaftar);
  const adaCsKc = daftar.tampil.some((k) => ["CS", "KEPALA_CABANG"].includes(akunMilik(k)?.peran));
  const lengkap = baru.employee_code.trim().length >= 3 && baru.full_name.trim() && baru.position.trim() && regionSaya;

  return (
    <div className="max-w-5xl space-y-4" data-testid="hr-karyawan-page">
      <div>
        <Link to="/hrd/rekap-absensi" className="text-xs text-muted-foreground underline">Rekap Absensi</Link>
        <h1 className="text-2xl font-semibold">Karyawan</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Semua orang yang wajib absen di cabang ini, termasuk Kepala Cabang. Isi nama dan jabatan; kode karyawan huruf besar (mis. SMD-001).
          {isKepalaCabang && " PIN absen kios diatur Kepala Cabang; PIN lama tidak pernah bisa dilihat."}
        </p>
      </div>

      <Card className="p-4 grid gap-2 sm:grid-cols-3" data-testid="hr-karyawan-baru">
        <Input placeholder="Kode (mis. SMD-001)" value={baru.employee_code} onChange={(e) => setBaru({ ...baru, employee_code: e.target.value })} data-testid="hk-kode" />
        <Input placeholder="Nama lengkap" value={baru.full_name} onChange={(e) => setBaru({ ...baru, full_name: e.target.value })} data-testid="hk-nama" />
        <Input placeholder="Jabatan" value={baru.position} onChange={(e) => setBaru({ ...baru, position: e.target.value })} data-testid="hk-jabatan" />
        {isOwner && (
          <select className="rounded-md border bg-background px-2 py-2 text-sm" value={baru.region_id}
                  onChange={(e) => setBaru({ ...baru, region_id: e.target.value, store_id: "" })} data-testid="hk-region">
            <option value="">— pilih wilayah —</option>
            {regions.map((r) => <option key={r.id} value={r.id}>{r.name || r.code}</option>)}
          </select>
        )}
        <select className="rounded-md border bg-background px-2 py-2 text-sm" value={baru.store_id}
                onChange={(e) => setBaru({ ...baru, store_id: e.target.value })} data-testid="hk-store">
          <option value="">— cabang (opsional) —</option>
          {storeRegion.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <Input placeholder="No. HP (opsional)" value={baru.phone} onChange={(e) => setBaru({ ...baru, phone: e.target.value })} />
        <div className="sm:col-span-3">
          <Button disabled={!lengkap || sibuk} onClick={simpan} data-testid="hk-simpan">Tambah karyawan</Button>
        </div>
      </Card>

      <Card className="p-3">
        <PalangDaftar daftar={daftar} placeholder="Cari nama / kode / jabatan…" testId="hk-palang"
                      ekspor={{ nama: "karyawan", kolom: KOLOM_CSV, ambilHalaman }}>
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={daftar.nilaiSaringan.aktif} onChange={(e) => daftar.setSaringan("aktif", e.target.value)}
                  aria-label="Saring status" data-testid="hk-filter-aktif">
            <option value="">Semua status</option>
            <option value="true">Aktif</option>
            <option value="false">Nonaktif</option>
          </select>
          {isOwner && (
            <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    value={daftar.nilaiSaringan.region || ""} onChange={(e) => daftar.setSaringan("region", e.target.value)}
                    aria-label="Saring cabang" data-testid="hk-filter-region">
              <option value="">Semua cabang</option>
              {regions.map((r) => <option key={r.id} value={r.id}>{r.name || r.code}</option>)}
            </select>
          )}
        </PalangDaftar>
      {bolehTaut && !memuat && adaCsKc && penerimaTg.length === 0 && (
        <p className="mb-2 rounded border border-warning-soft-border bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground"
           data-testid="hk-peringatan-telegram">
          Belum ada CS atau Kepala Cabang di daftar ini yang terdaftar Telegram. Tugas "WhatsApp ke customer belum sampai"
          tidak akan sampai lewat Telegram — tekan "Tautan Telegram" pada baris orangnya.
        </p>
      )}
      {tautanTg && (
        <div className="mb-2 rounded-lg border border-border bg-muted/50 p-3 text-sm" data-testid="hk-tautan-telegram">
          <p className="font-medium">Tautan pendaftaran Telegram untuk {tautanTg.nama}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Salin seluruh pesan di bawah dan kirim ke orangnya lewat WhatsApp. <strong>Ia sendiri yang harus
            mengetuk tautannya</strong> dari HP yang memakai Telegram. Bila bot hanya membalas petunjuk, ia menyalin
            baris /daftar ke bot. Berlaku {tautanTg.jam} jam.
          </p>
          {/* T114: tautan + cadangan /daftar dalam satu pesan siap kirim. */}
          <textarea readOnly rows={5} value={pesanPendaftaranTelegram(tautanTg)} onFocus={(e) => e.target.select()}
                    data-testid="hk-pesan-telegram"
                    className="mt-2 w-full rounded-md border bg-background px-2 py-1 font-mono text-xs" />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><ThUrut daftar={daftar} kunci="employee_code" className="px-3 py-2 text-left">Kode</ThUrut>
              <ThUrut daftar={daftar} kunci="full_name" className="px-3 py-2 text-left">Nama</ThUrut>
              <ThUrut daftar={daftar} kunci="position" className="px-3 py-2 text-left">Jabatan</ThUrut>
              <th className="px-3 py-2 text-left">Akun login</th>
              <th className="px-3 py-2 text-left">PIN kios</th>
              <th className="px-3 py-2 text-left">Telegram</th>
              <ThUrut daftar={daftar} kunci="is_active" className="px-3 py-2 text-left">Status</ThUrut></tr>
          </thead>
          <tbody>
            {memuat && daftar.tampil.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Memuat…</td></tr>}
            {!memuat && daftar.total === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
              {daftar.cari.trim() || daftar.saringanAktif.some((x) => !x.bawaan) ? "Tidak ada yang cocok." : "Belum ada karyawan."}</td></tr>}
            {daftar.tampil.map((k) => {
              const milik = akunMilik(k);
              return (
                <tr key={k.id} className="border-t align-top" data-testid={`hk-baris-${k.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{k.employee_code}</td>
                  <td className="px-3 py-2">{k.full_name}</td>
                  <td className="px-3 py-2">{k.position}</td>
                  <td className="px-3 py-2 text-xs">
                    {k.user_profile_id ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <span data-testid={`hk-akun-${k.id}`}>{milik ? milik.email : "tertaut"}</span>
                        {bolehTaut && (milik || isOwner) && (
                          <Button size="sm" variant="outline" onClick={() => lepas(k)} data-testid={`hk-lepas-${k.id}`}>Lepas</Button>
                        )}
                      </div>
                    ) : bolehTaut ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <select className="max-w-[14rem] rounded-md border bg-background px-2 py-1 text-xs"
                                value={pilihAkun[k.id] || ""} onChange={(e) => setPilihAkun({ ...pilihAkun, [k.id]: e.target.value })}
                                data-testid={`hk-pilih-akun-${k.id}`}>
                          <option value="">— pilih akun —</option>
                          {akunBebas.map((a) => (
                            <option key={a.id} value={a.id}>{a.email} ({NAMA_PERAN[a.peran] || "tanpa peran"})</option>
                          ))}
                        </select>
                        <Button size="sm" disabled={!pilihAkun[k.id]} onClick={() => tautkan(k, pilihAkun[k.id])}
                                data-testid={`hk-tautkan-${k.id}`}>Tautkan</Button>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span data-testid={`hk-status-pin-${k.id}`}>{k.punya_pin ? "sudah diatur" : "belum ada"}</span>
                    {isKepalaCabang && pinId !== k.id && (
                      <Button size="sm" variant="outline" className="ml-2" onClick={() => setPinId(k.id)}
                              data-testid={`hk-pin-${k.id}`}>{k.punya_pin ? "Ganti PIN" : "Atur PIN"}</Button>
                    )}
                    {isKepalaCabang && pinId === k.id && <AturPin k={k} onSelesai={() => { setPinId(null); muat(); }} />}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span data-testid={`hk-status-tg-${k.id}`}>{k.telegram_terdaftar ? "terdaftar" : "belum"}</span>
                    {bolehTaut && !k.telegram_terdaftar && k.is_active !== false && (
                      <Button size="sm" variant="outline" className="ml-2" onClick={() => mintaTautanTg(k)}
                              data-testid={`hk-tautan-tg-${k.id}`}>Tautan Telegram</Button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{k.is_active === false ? "nonaktif" : "aktif"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="hk-paginasi" />
      </div>
      </Card>

      {isOwner && <TabletKios regions={regions} stores={stores} />}
    </div>
  );
}
